<template>
  <main class="main main--has-floating-buttons">
    <template v-if="dataLoaded === true">
      <portal to="headerTitle">
        {{ template.name }}
      </portal>
      <card :title="$t(`admin.pageTemplate.card.general`)" cols="12 6@desktop">
        <hm-card-inner>
          <hm-input v-model="template.name"
                    :lazy="true"
                    :disabled="template.deleted"
                    :label="$t('admin.pageTemplate.name')"
                    validate="required"
                    name="name"
                    cols="12" />
          <template v-if="template.documentType.endsWith('Email')">
            <hm-input-select v-model="template.emailTemplateType"
                             :disabled="template.deleted"
                             :label="$t('admin.pageTemplate.emailTemplateType')"
                             name="emailTemplateType"
                             cols="12 start-1"
                             enum-name="EmailTemplateType" />
          </template>
          <template v-if="template.documentType === 'GenericDocument'">
            <hm-input-select v-model="template.documentTemplateType"
                             :disabled="template.deleted"
                             :label="$t('admin.pageTemplate.documentTemplateType')"
                             name="documentTemplateType"
                             cols="12 start-1"
                             enum-name="DocumentTemplateType" />
          </template>
        </hm-card-inner>
      </card>
      <card v-if="template.documentType.endsWith('Email')" :title="$t(`admin.pageTemplate.card.attachments`)" cols="12 6@desktop">
        <hm-card-inner>
          <hm-input-file v-model="template.attachment1"
                         :url="serviceUrls.template.uploadFile(this, templateId, 'attachment1')"
                         :label="$t('admin.pageTemplate.attachment1')"
                         :help="$t('admin.pageTemplate.attachment_help')"
                         cols="12" />
          <hm-input-file v-model="template.attachment2"
                         :url="serviceUrls.template.uploadFile(this, templateId, 'attachment2')"
                         :label="$t('admin.pageTemplate.attachment2')"
                         :help="$t('admin.pageTemplate.attachment_help')"
                         cols="12" />
          <hm-input-file v-model="template.attachment3"
                         :url="serviceUrls.template.uploadFile(this, templateId, 'attachment3')"
                         :label="$t('admin.pageTemplate.attachment3')"
                         :help="$t('admin.pageTemplate.attachment_help')"
                         cols="12" />
        </hm-card-inner>
      </card>
      <tabable-card cols="12">
        <tabable-card-content :title="$t(`lang.${hotelSettings.details.primaryLanguage}`)">
          <hm-card-inner>
            <hm-input v-model="template.title[hotelSettings.details.primaryLanguage]"
                      :lazy="true"
                      :disabled="template.deleted"
                      :label="$t('admin.pageTemplate.title')"
                      validate="required"
                      name="title"
                      cols="12" />
          </hm-card-inner>
          <hm-card-inner :full-size="true">
            <hm-col cols="12">
              <ckeditor v-model="template.templateContent[hotelSettings.details.primaryLanguage]" :incard="true" height="500" />
            </hm-col>
          </hm-card-inner>
        </tabable-card-content>
        <tabable-card-content v-for="lang in hotelSettings.details.additionalLanguages" :title="$t(`lang.${lang}`)" :key="lang">
          <hm-card-inner>
            <hm-input v-model="template.title[lang]"
                      :lazy="true"
                      :disabled="template.deleted"
                      :label="$t('admin.pageTemplate.title')"
                      name="title"
                      cols="12" />
          </hm-card-inner>
          <hm-card-inner :full-size="true">
            <hm-col cols="12">
              <ckeditor :ref="`editor_${lang}`"
                        :id="`editor_${lang}`"
                        v-model="template.templateContent[lang]"
                        :incard="true"
                        height="500" />
            </hm-col>
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>

      <ai-text-generator />

      <hm-floating-buttons id="actions" ref="floatingButtons">
        <template v-if="!template.deleted && template.documentType === 'GuestOfferDocument'">
          <hm-floating-button label="AI Text"
                              icon="magic-wand"
                              color="blue">
            <hm-floating-child-button label="Intro Text"
                                      icon="magic-wand"
                                      color="blue"
                                      @click="generateIntroText"/>
            <hm-floating-child-button label="Inklusivleistungen"
                                      icon="magic-wand"
                                      color="blue"
                                      @click="generateIncludedServices"/>
            <hm-floating-child-button label="Highlights"
                                      icon="magic-wand"
                                      color="blue"
                                      @click="generateHighlights"/>
          </hm-floating-button>
        </template>
        <hm-floating-button v-if="!template.deleted && canDelete"
                            :label="$t('admin.pageTemplate.actions.deleteTemplate')"
                            icon="delete"
                            color="error"
                            @click="deleteTemplate(false)" />
      </hm-floating-buttons>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, groupBy, isEmpty, isNil, keyBy, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmFloatingButton from '../../lib/inputs/HmFloatingButton';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmMultiselect from '../../lib/inputs/HmMultiselect';
import TemplateMixin from '../../../mixins/TemplateMixin';
import HmFloatingChildButton from '../../lib/inputs/HmFloatingChildButton';

export default {
  name: 'PageTemplate',
  components: { HmFloatingChildButton, HmMultiselect, HmCardInner, HmFloatingButton },
  autosave: [
    {
      name: 'template',
      variable: 'template',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        if (this.template.deleted === true) return;

        const toSave = cloneDeep(this.template);

        this.axios.put(this.serviceUrls.template.get(this, this.templateId), toSave)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();

            this.$serviceBus.$emit('template.list');
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin, TemplateMixin],

  data() {
    return {
      template: {
        rateMode: null,
        rateFrequency: null,
      },
      templateId: this.$route.params.templateId,
      dataLoaded: false,
    };
  },

  computed: {
    canDelete: function () {
      const ret = !isNil(this.template.id) && !isNil(this.templateDocuments) && this.templateDocuments[this.template.documentType].canHaveMultiple;

      if (!ret) return false;

      switch (ret.documentType) {
        case 'GenericDocument':
        case 'GenericEmail':
          return true;
        case 'ReservationConfirmationEmail':
          if (this.template.id === this.hotelSettings.details.onlineBookingSettings.defaultReservationConfirmationTemplateId) return false;
          return !isNil(this.templates)
            && this.templates
              .filter(it => it.deleted === false)
              .filter(it => it.documentType === this.template.documentType)
              .length > 1;
        default:
          return !isNil(this.templates)
            && this.templates
              .filter(it => it.deleted === false)
              .filter(it => it.documentType === this.template.documentType)
              .length > 1;
      }
    },
  },

  watch: {
    template: function (newVal) {
      if (isNil(newVal)) {
        this.dataLoaded = false;
      }
    },
  },

  created: function () {
    this.loadData();
    this.$serviceBus.$emit('template.list');
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    routeToRatePlan(ratePlan) {
      this.$router.push({
        name: 'ratePlan',
        params: {
          ratePlanId: ratePlan.id,
        },
      });
    },
    routeToRoomType(roomType) {
      this.$router.push({
        name: 'roomType',
        params: {
          roomTypeId: roomType.id,
        },
      });
    },
    loadData() {
      this.axios.get(this.serviceUrls.template.get(this, this.templateId))
        .then((response) => {
          if (this.dataLoaded === false) {
            this.template = response.data.data;
            if (isNil(this.template.title)) this.template.title = {};
            if (isNil(this.template.templateContent)) this.template.templateContent = {};
            this.dataLoaded = true;
          }
        });
    },
    deleteTemplate(confirmed = false) {
      if (this.canDelete) {
        if (confirmed === true) {
          this.axios.delete(this.serviceUrls.template.get(this, this.templateId))
            .then((response) => {
              this.dataLoaded = false;
              this.template = response.data.data;
              this.showSuccessMessage();
              this.$serviceBus.$emit('hotelSettings.update');
              this.$serviceBus.$emit('template.list');
              this.$router.push({
                name: 'templates',
              });
            })
            .catch((error) => {
              this.showMessages(error.response);
            })
            .catch((error) => {
              if (isNil(error.response)) this.showErrorMessage();
              this.showMessages(error.response);
            });
        } else {
          this.showConfirmModal(
            this.$t('admin.pageTemplate.confirmDeleteTemplate'),
            (doNotShowAgain) => {
              this.deleteTemplate(true);
            },
          );
        }
      } else {
        this.showErrorMessage(this.$t('admin.pageTemplate.canNotDelete'));
      }
    },
    generateIntroText() {
      this.$serviceBus.$emit('aiTextGenerator.show', this.serviceUrls.aiTextGenerator.hotelOfferIntro(this));
    },
    generateHighlights() {
      this.$serviceBus.$emit('aiTextGenerator.show', this.serviceUrls.aiTextGenerator.hotelHighlights(this));
    },
    generateIncludedServices() {
      this.$serviceBus.$emit('aiTextGenerator.show', this.serviceUrls.aiTextGenerator.hotelIncludedServices(this));
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageTemplate.vue